import { useEffect, useRef, useState, type ReactNode } from 'react'
import { LENGTHS, pageButtons } from '../ui/DataTable'

/**
 * Tabelul unei file din randul desfacut - `initDetailsTable` din securitee.js
 * (DataTables serverSide cu cautare, lungime, paginare), cu markupul pe care
 * il scrie DataTables. Coloanele se ordoneaza dupa `key`.
 */

export interface NestedColumn {
  key: string
  title: string
  titleHtml?: string
  html(row: Record<string, unknown>): string
  /**
   * Celula ca componenta, nu ca text - pentru o bifa care trebuie sa fie
   * `SettingsCheck` (Block din Productivitee > Restrictions). Cand e data,
   * `html` nu se mai foloseste pentru celula.
   */
  render?(row: Record<string, unknown>): ReactNode
  /** `orderable: false` din columnDefs: capul nu mai sorteaza. Implicit da. */
  orderable?: boolean
}

export interface NestedState {
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  search: string
}

export function NestedTable({ id, columns, rows, total, fetching, error, state, onState }: {
  id: string
  columns: NestedColumn[]
  rows: Record<string, unknown>[]
  total: number
  fetching: boolean
  error: string | null
  state: NestedState
  onState(next: NestedState): void
}) {
  const { start, length, order, search } = state
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)
  useEffect(() => setDraft(search), [search])
  useEffect(() => () => { if (typing.current !== null) window.clearTimeout(typing.current) }, [])

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${id}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${id}_filter`} className="dataTables_filter input-icon">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={id}
                value={draft}
                onChange={(e) => {
                  const value = e.target.value
                  setDraft(value)
                  if (typing.current !== null) window.clearTimeout(typing.current)
                  typing.current = window.setTimeout(() => onState({ ...state, search: value, start: 0 }), 400)
                }}
              />
            </label>
          </div>
        </div>
        <div className="col-sm-6 dataTableA"></div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${id}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable events-table no-footer" width="100%" id={id} role="grid">
            <thead>
              <tr role="row">
                {columns.map((col, i) => {
                  const sorted = order.column === i
                  const orderable = col.orderable !== false
                  return (
                    <th
                      key={`${col.key}-${i}`}
                      className={`td-small a-left ${!orderable ? 'sorting_disabled' : sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting'}`}
                      aria-sort={orderable && sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={orderable ? () => onState({ ...state, start: 0, order: { column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' } }) : undefined}
                    >
                      {col.titleHtml ? <span dangerouslySetInnerHTML={{ __html: col.titleHtml }} /> : col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd"><td colSpan={columns.length} className="dataTables_empty">Something went wrong: {error}</td></tr>
              ) : rows.length === 0 ? (
                <tr className="odd"><td colSpan={columns.length} className="dataTables_empty">No data available in table</td></tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={String(row.DT_RowId ?? i)} id={String(row.DT_RowId ?? '')} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {columns.map((col, c) => (col.render
                      ? <td key={`${col.key}-${c}`} className="td-small a-left">{col.render(row)}</td>
                      : <td key={`${col.key}-${c}`} className="td-small a-left" dangerouslySetInnerHTML={{ __html: col.html(row) }} />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              Show{' '}
              <select name={`${id}_length`} aria-controls={id} className="form-control input-sm" value={length} onChange={(e) => onState({ ...state, length: Number(e.target.value), start: 0 })}>
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`}>
                <a href="#" onClick={(e) => { e.preventDefault(); if (page > 0) onState({ ...state, start: (page - 1) * length }) }}>Previous</a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled"><a href="#" onClick={(e) => e.preventDefault()}>…</a></li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a href="#" onClick={(e) => { e.preventDefault(); onState({ ...state, start: b * length }) }}>{b + 1}</a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`}>
                <a href="#" onClick={(e) => { e.preventDefault(); if (page < pages - 1) onState({ ...state, start: (page + 1) * length }) }}>Next</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
